/**
 * Centralized Google Maps API loader service
 * Prevents multiple loading of the Google Maps JavaScript API
 */

const GOOGLE_MAPS_API_KEY = 'AIzaSyDXPxv0rzBHqcXZlnxoZZ7yvwzzynY4yF4';
const GOOGLE_MAPS_API_URL = 'https://maps.googleapis.com/maps/api/js';

// Global state to track loading
let isLoaded = false;
let isLoading = false;
let loadPromise = null;

/**
 * Load Google Maps API with specified libraries
 * @param {string[]} libraries - Array of libraries to load (e.g., ['places'])
 * @param {string} callback - Optional callback function name
 * @returns {Promise} Promise that resolves when API is loaded
 */
export function loadGoogleMapsAPI(libraries = [], callback = null) {
  // If already loaded, resolve immediately
  if (isLoaded && window.google && window.google.maps) {
    console.log('🗺️ Google Maps API already loaded');
    return Promise.resolve();
  }

  // If currently loading, return the existing promise
  if (isLoading && loadPromise) {
    console.log('🗺️ Google Maps API loading in progress, waiting...');
    return loadPromise;
  }

  // Start loading
  console.log('🗺️ Loading Google Maps API with libraries:', libraries);
  isLoading = true;

  loadPromise = new Promise((resolve, reject) => {
    // Check if script already exists
    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      console.log('🗺️ Google Maps script already exists in DOM');
      // Wait for it to load if not already loaded
      if (window.google && window.google.maps) {
        isLoaded = true;
        isLoading = false;
        resolve();
        return;
      }
    }

    // Create script element
    const script = document.createElement('script');
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
    });

    if (libraries.length > 0) {
      params.set('libraries', libraries.join(','));
    }

    if (callback) {
      params.set('callback', callback);
      // Make callback available globally if provided
      window[callback] = () => {
        isLoaded = true;
        isLoading = false;
        console.log('🗺️ Google Maps API loaded via callback');
        resolve();
      };
    }

    script.src = `${GOOGLE_MAPS_API_URL}?${params.toString()}`;
    script.defer = true;

    script.onload = () => {
      if (!callback) {
        // No callback, check if API is available
        const checkLoaded = () => {
          if (window.google && window.google.maps) {
            isLoaded = true;
            isLoading = false;
            console.log('🗺️ Google Maps API loaded via onload');
            resolve();
          } else {
            // Retry after a short delay
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      }
    };

    script.onerror = (error) => {
      isLoading = false;
      console.error('🗺️ Failed to load Google Maps API:', error);
      reject(new Error('Failed to load Google Maps API'));
    };

    // Only append if no existing script
    if (!existingScript) {
      document.head.appendChild(script);
      console.log('🗺️ Google Maps API script added to DOM');
    }
  });

  return loadPromise;
}

/**
 * Check if Google Maps API is loaded
 * @returns {boolean}
 */
export function isGoogleMapsLoaded() {
  return isLoaded && window.google && window.google.maps;
}

/**
 * Wait for Google Maps API to be loaded
 * @returns {Promise}
 */
export function waitForGoogleMaps() {
  if (isGoogleMapsLoaded()) {
    return Promise.resolve();
  }
  
  if (isLoading && loadPromise) {
    return loadPromise;
  }
  
  // If not loading and not loaded, start loading with minimal setup
  return loadGoogleMapsAPI();
}

/**
 * Load Google Maps API for Places functionality
 * @returns {Promise}
 */
export function loadGooglePlaces() {
  return loadGoogleMapsAPI(['places']);
}

/**
 * Load Google Maps API for basic map functionality
 * @param {string} callback - Optional callback function name
 * @returns {Promise}
 */
export function loadGoogleMaps(callback = null) {
  return loadGoogleMapsAPI([], callback);
}